class CollectionFilterForm extends HTMLElement {
  constructor() {
    super();
    
    // Use a minimal debounce to prevent rapid-fire filtering
    this.debouncedOnSubmit = typeof debounce !== 'undefined' 
      ? debounce((event) => { this.onSubmitHandler(event); }, 100)
      : null;
    
    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('change', (e) => {
        if (e.target && e.target.classList.contains('collection-filter-checkbox')) {
          // Immediate filtering - no debounce for better responsiveness
          this.onSubmitHandler(e);
        }
      });
    }
    
    // Listen directly to checkbox changes for immediate response
    const checkboxes = this.querySelectorAll('.collection-filter-checkbox');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', (e) => {
        e.stopPropagation();
        this.onSubmitHandler(e);
      });
    });
  }

  getSelectedCollectionHandles() {
    const checked = this.querySelectorAll('.collection-filter-checkbox:checked');
    return Array.from(checked).map(cb => cb.dataset.collectionHandle).filter(Boolean);
  }

  onSubmitHandler(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Update URL first to persist state
    this.updateURL();
    
    // Then filter immediately
    this.filterProducts();
  }

  filterProducts() {
    // Prevent infinite loops
    if (this._isFiltering) return;
    this._isFiltering = true;
    
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) {
      this._isFiltering = false;
      return;
    }

    const productItems = Array.from(productGrid.querySelectorAll('.grid__item, li[class*="grid__item"]'));
    
    if (productItems.length === 0) {
      this._isFiltering = false;
      return;
    }

    const selectedHandles = this.getSelectedCollectionHandles();
    
    // Reset to page 1 when filtering changes
    window.collectionFilterCurrentPage = 1;
    
    // Pre-process selected handles for faster lookup
    const selectedHandlesSet = new Set(selectedHandles.map(h => h.trim()));
    
    // Temporarily disconnect observer to prevent infinite loop
    if (window.paginationObserver) {
      window.paginationObserver.disconnect();
    }
    
    // If no collections selected, show all products
    if (selectedHandlesSet.size === 0) {
      productItems.forEach(item => {
        item.classList.remove('hidden', 'pagination-hidden');
        item.removeAttribute('hidden');
      });
      this.updateProductCount(productItems.length);
      this.updatePagination(visibleCount || productItems.length, productItems.length);
      this.hideEmptyState(productGrid);
      
      // Reconnect observer
      if (window.paginationObserver && productGrid) {
        window.paginationObserver.observe(productGrid, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'hidden']
        });
      }
      this._isFiltering = false;
      return;
    }

    // Filter products based on collection membership - optimized
    let visibleCount = 0;
    productItems.forEach(item => {
      const collectionsAttr = item.dataset.collections || '';
      
      if (!collectionsAttr) {
        item.style.display = 'none';
        item.classList.add('hidden');
        item.setAttribute('hidden', 'true');
        return;
      }

      // Fast check using Set for O(1) lookup
      const productCollections = collectionsAttr.split(',');
      let belongsToSelected = false;
      
      for (let i = 0; i < productCollections.length; i++) {
        if (selectedHandlesSet.has(productCollections[i].trim())) {
          belongsToSelected = true;
          break;
        }
      }
      
      if (belongsToSelected) {
        item.classList.remove('hidden', 'pagination-hidden');
        item.removeAttribute('hidden');
        visibleCount++;
      } else {
        item.style.display = 'none';
        item.classList.add('hidden');
        item.setAttribute('hidden', 'true');
      }
    });

    // Update product count with filtered count
    this.updateProductCount(visibleCount);
    this.updatePagination(visibleCount, productItems.length);
    
    // Show/hide empty state
    if (visibleCount === 0) {
      this.showEmptyState(productGrid);
    } else {
      this.hideEmptyState(productGrid);
    }
    
    // Reconnect observer after a short delay
    setTimeout(() => {
      if (window.paginationObserver && productGrid) {
        window.paginationObserver.observe(productGrid, {
          childList: true,
          subtree: false  // Only watch direct children
        });
      }
      this._isFiltering = false;
    }, 50);
  }

  updatePagination(visibleCount, totalItems) {
    // Store visible count for pagination
    window.collectionFilterVisibleCount = visibleCount;
    
    // Get products per page from section settings or default to 24
    const productsPerPageElement = document.querySelector('[data-products-per-page]');
    const productsPerPage = productsPerPageElement ? parseInt(productsPerPageElement.dataset.productsPerPage) || 24 : 24;
    
    const totalPages = Math.ceil(visibleCount / productsPerPage);
    
    // Reset to page 1 when filters change
    window.collectionFilterCurrentPage = 1;
    const currentPage = 1;
    
    // Remove existing pagination wrapper if any
    const existingPaginationWrapper = document.querySelector('.pagination-wrapper');
    if (existingPaginationWrapper) {
      existingPaginationWrapper.remove();
    }
    
    // Show first page of filtered results immediately
    this.showPaginationPage(1, productsPerPage);
    
    // Only create pagination UI if there are more products than per page
    if (totalPages <= 1) {
      return;
    }
    
    // Load pagination CSS if not already loaded
    if (!document.querySelector('link[href*="component-pagination.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = '/assets/component-pagination.css';
      document.head.appendChild(link);
    }
    
    // Create pagination UI
    const paginationWrapper = document.createElement('div');
    paginationWrapper.className = 'pagination-wrapper';
    paginationWrapper.setAttribute('data-page', currentPage);
    
    const paginationContainer = document.createElement('nav');
    paginationContainer.className = 'collection-filter-pagination pagination';
    paginationContainer.setAttribute('role', 'navigation');
    paginationContainer.setAttribute('aria-label', 'Pagination');
    
    const paginationList = document.createElement('ul');
    paginationList.className = 'pagination__list list-unstyled';
    paginationList.setAttribute('role', 'list');
    
    // Previous button
    if (currentPage > 1) {
      const prevLi = document.createElement('li');
      const prevLink = document.createElement('a');
      prevLink.href = '#';
      prevLink.className = 'pagination__item pagination__item--next pagination__item-arrow link motion-reduce';
      prevLink.setAttribute('aria-label', 'Previous page');
      const svgWrapper = document.createElement('span');
      svgWrapper.className = 'svg-wrapper';
      // Try to get icon from existing pagination or create simple arrow
      const existingIcon = document.querySelector('.pagination__item-arrow .svg-wrapper')?.innerHTML;
      if (existingIcon) {
        svgWrapper.innerHTML = existingIcon;
      } else {
        // Create a simple arrow using SVG or text
        svgWrapper.innerHTML = '‹';
      }
      prevLink.appendChild(svgWrapper);
      prevLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.showPaginationPage(currentPage - 1, productsPerPage);
      });
      prevLi.appendChild(prevLink);
      paginationList.appendChild(prevLi);
    }
    
    // Page numbers
    for (let i = 1; i <= totalPages; i++) {
      const pageLi = document.createElement('li');
      const pageLink = document.createElement('a');
      pageLink.href = '#';
      pageLink.className = i === currentPage ? 'pagination__item pagination__item--current light' : 'pagination__item link';
      pageLink.textContent = i;
      pageLink.setAttribute('aria-label', `Page ${i}`);
      if (i === currentPage) {
        pageLink.setAttribute('aria-current', 'page');
      }
      pageLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.showPaginationPage(i, productsPerPage);
      });
      pageLi.appendChild(pageLink);
      paginationList.appendChild(pageLi);
    }
    
    // Next button
    if (currentPage < totalPages) {
      const nextLi = document.createElement('li');
      const nextLink = document.createElement('a');
      nextLink.href = '#';
      nextLink.className = 'pagination__item pagination__item--prev pagination__item-arrow link motion-reduce';
      nextLink.setAttribute('aria-label', 'Next page');
      const svgWrapper = document.createElement('span');
      svgWrapper.className = 'svg-wrapper';
      const existingIcon = document.querySelector('.pagination__item-arrow .svg-wrapper')?.innerHTML;
      if (existingIcon) {
        svgWrapper.innerHTML = existingIcon;
      } else {
        svgWrapper.innerHTML = '›';
      }
      nextLink.appendChild(svgWrapper);
      nextLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.showPaginationPage(currentPage + 1, productsPerPage);
      });
      nextLi.appendChild(nextLink);
      paginationList.appendChild(nextLi);
    }
    
    paginationContainer.appendChild(paginationList);
    paginationWrapper.appendChild(paginationContainer);
    
    // Insert pagination after product grid
    const productGrid = document.getElementById('product-grid');
    if (productGrid && productGrid.parentElement) {
      productGrid.parentElement.appendChild(paginationWrapper);
    }
    
    // Update pagination UI to reflect current page
    this.updatePaginationUI(currentPage, totalPages, productsPerPage);
  }
  
  updatePaginationUI(currentPage, totalPages, productsPerPage) {
    const pagination = document.querySelector('.collection-filter-pagination');
    if (!pagination) return;
    
    // Update current page highlighting
    const allPageLinks = pagination.querySelectorAll('.pagination__item');
    allPageLinks.forEach(link => {
      const pageNum = parseInt(link.textContent.trim());
      if (pageNum === currentPage) {
        link.classList.add('pagination__item--current');
        link.classList.remove('link');
        link.setAttribute('aria-current', 'page');
      } else if (pageNum) {
        link.classList.remove('pagination__item--current');
        if (!link.classList.contains('pagination__item-arrow')) {
          link.classList.add('link');
        }
        link.removeAttribute('aria-current');
      }
    });
    
    // Update previous/next buttons visibility
    const prevButton = pagination.querySelector('.pagination__item--next.pagination__item-arrow');
    const nextButton = pagination.querySelector('.pagination__item--prev.pagination__item-arrow');
    
    if (prevButton) {
      prevButton.closest('li').style.display = currentPage > 1 ? '' : 'none';
    }
    if (nextButton) {
      nextButton.closest('li').style.display = currentPage < totalPages ? '' : 'none';
    }
    
    // Update wrapper data attribute
    const wrapper = pagination.closest('.pagination-wrapper');
    if (wrapper) {
      wrapper.setAttribute('data-page', currentPage);
    }
  }

  showPaginationPage(page, productsPerPage) {
    window.collectionFilterCurrentPage = page;
    
    const productGrid = document.getElementById('product-grid');
    if (!productGrid) return;
    
    const productItems = Array.from(productGrid.querySelectorAll('.grid__item, li[class*="grid__item"]'));
    const visibleItems = productItems.filter(item => 
      !item.classList.contains('hidden')
    );
    
    const startIndex = (page - 1) * productsPerPage;
    const endIndex = startIndex + productsPerPage;
    
    // Show/hide products based on current page
    visibleItems.forEach((item, index) => {
      if (index >= startIndex && index < endIndex) {
        item.style.display = '';
        item.classList.remove('pagination-hidden');
        item.removeAttribute('hidden');
      } else {
        item.style.display = 'none';
        item.classList.add('pagination-hidden');
        item.setAttribute('hidden', 'true');
      }
    });
    
    // Update pagination UI
    const totalPages = Math.ceil(visibleItems.length / productsPerPage);
    this.updatePaginationUI(page, totalPages, productsPerPage);
    
    // Update product count to show current page range
    const visibleOnPage = Math.min(endIndex - startIndex, visibleItems.length - startIndex);
    this.updateProductCount(visibleItems.length, `${startIndex + 1}-${Math.min(endIndex, visibleItems.length)}`);
  }

  hideEmptyState(productGrid) {
    const emptyState = productGrid.closest('.collection')?.querySelector('.collection--empty:not(.collection--empty-permanent)');
    if (emptyState) {
      emptyState.remove();
    }
  }

  showEmptyState(container) {
    const collectionDiv = container.closest('.collection');
    if (!collectionDiv) return;
    
    // Check if empty state already exists
    let emptyDiv = collectionDiv.querySelector('.collection--empty:not(.collection--empty-permanent)');
    
    if (!emptyDiv) {
      emptyDiv = document.createElement('div');
      emptyDiv.className = 'collection--empty page-width';
      emptyDiv.innerHTML = `
        <div class="title-wrapper center">
          <h2 class="title title--primary">No products found matching the selected collections.</h2>
        </div>
      `;
      collectionDiv.appendChild(emptyDiv);
    }
  }

  updateProductCount(count, range) {
    // Update product count elements
    const countElements = document.querySelectorAll('#ProductCount, #ProductCountDesktop, .product-count__text');
    countElements.forEach(el => {
      if (el) {
        // Only update text nodes, not entire innerHTML
        const textNode = Array.from(el.childNodes).find(node => node.nodeType === Node.TEXT_NODE);
        if (range && count > 0) {
          el.textContent = `Showing ${range} of ${count} product${count !== 1 ? 's' : ''}`;
        } else if (count > 0) {
          el.textContent = `${count} product${count !== 1 ? 's' : ''}`;
        }
      }
    });
    
    // Also update span inside if it exists
    const countSpans = document.querySelectorAll('#ProductCount span, #ProductCountDesktop span');
    countSpans.forEach(span => {
      if (range && count > 0) {
        span.textContent = `Showing ${range} of ${count} product${count !== 1 ? 's' : ''}`;
      } else if (count > 0) {
        span.textContent = `${count} product${count !== 1 ? 's' : ''}`;
      }
    });
  }

  updateURL() {
    // Get all checkboxes from the form (not just this element's children)
    const form = this.closest('collection-filter-form') || document.querySelector('collection-filter-form') || this;
    const checkboxes = form.querySelectorAll ? form.querySelectorAll('.collection-filter-checkbox') : this.querySelectorAll('.collection-filter-checkbox');
    
    const selectedHandles = Array.from(checkboxes)
      .filter(cb => cb.checked)
      .map(cb => cb.dataset.collectionHandle)
      .filter(Boolean);

    const url = new URL(window.location);
    
    if (selectedHandles.length > 0) {
      url.searchParams.set('collections', selectedHandles.join(','));
    } else {
      url.searchParams.delete('collections');
    }

    window.history.pushState({}, '', url);
    
    // Update active facets display
    this.updateActiveFacets(selectedHandles);
  }

  updateActiveFacets(selectedHandles) {
    // Active facets section has been removed - no need to show pills
    // Just remove any existing pills if they exist
    const existingPills = document.querySelectorAll('.active-facet-collection');
    existingPills.forEach(pill => pill.remove());
    return;
    
    // Add pills for selected collections
    if (selectedHandles.length > 0) {
      // Get icon SVG from existing pills if available
      const existingPill = activeFacetsDesktop?.querySelector('.active-facets__button') || 
                          activeFacetsMobile?.querySelector('.active-facets__button');
      const svgWrapper = existingPill?.querySelector('.svg-wrapper');
      const iconHTML = svgWrapper ? svgWrapper.innerHTML : '';
      
      selectedHandles.forEach(handle => {
        // Find the collection name from the checkbox
        const checkbox = this.querySelector(`input[data-collection-handle="${handle}"]`);
        if (!checkbox) return;
        
        const label = checkbox.closest('label');
        const collectionName = label?.querySelector('.facet-checkbox__text-label')?.textContent?.trim() || 
                              label?.querySelector('.facet-checkbox__text')?.textContent?.trim() ||
                              handle;
        
        // Create active facet pill for desktop
        if (activeFacetsDesktop) {
          const pill = document.createElement('facet-remove');
          pill.className = 'active-facet-collection';
          
          const link = document.createElement('a');
          link.href = '#';
          link.className = 'active-facets__button active-facets__button--light';
          const checkboxToUncheck = checkbox; // Capture checkbox reference
          const form = this; // Capture form reference
          link.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Uncheck the checkbox first
            if (checkboxToUncheck) {
              checkboxToUncheck.checked = false;
              
              // Small delay to ensure checkbox state is updated, then trigger filtering
              // The change event will be caught by event delegation and call filterProductsByCollections
              // which will handle filtering and URL update
              setTimeout(() => {
                // Create and dispatch change event to trigger filtering via event delegation
                const changeEvent = new Event('change', { 
                  bubbles: true, 
                  cancelable: true 
                });
                checkboxToUncheck.dispatchEvent(changeEvent);
              }, 10);
            }
          });
          
          const buttonInner = document.createElement('span');
          buttonInner.className = 'active-facets__button-inner button button--tertiary';
          
          if (iconHTML) {
            buttonInner.innerHTML = `
              Collections: ${this.escapeHtml(collectionName)}
              <span class="svg-wrapper">
                ${iconHTML}
              </span>
              <span class="visually-hidden">Clear filter</span>
            `;
          } else {
            buttonInner.innerHTML = `
              Collections: ${this.escapeHtml(collectionName)}
              <span class="svg-wrapper">×</span>
              <span class="visually-hidden">Clear filter</span>
            `;
          }
          
          link.appendChild(buttonInner);
          pill.appendChild(link);
          
          // Insert after the vertical-filter wrapper div (where other filter pills are)
          const verticalFilterWrapper = activeFacetsDesktop.querySelector('.active-facets-vertical-filter');
          
          if (verticalFilterWrapper) {
            // Insert after the wrapper, alongside other filter pills
            verticalFilterWrapper.after(pill);
          } else {
            // Fallback: append to container
            activeFacetsDesktop.appendChild(pill);
          }
        }
        
        // Create active facet pill for mobile
        if (activeFacetsMobile) {
          const pill = document.createElement('facet-remove');
          pill.className = 'active-facet-collection';
          
          const link = document.createElement('a');
          link.href = '#';
          link.className = 'active-facets__button active-facets__button--light';
          const checkboxToUncheck = checkbox; // Capture checkbox reference
          const form = this; // Capture form reference
          link.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            // Uncheck the checkbox first
            if (checkboxToUncheck) {
              checkboxToUncheck.checked = false;
              
              // Small delay to ensure checkbox state is updated, then trigger filtering
              // The change event will be caught by event delegation and call filterProductsByCollections
              // which will handle filtering and URL update
              setTimeout(() => {
                // Create and dispatch change event to trigger filtering via event delegation
                const changeEvent = new Event('change', { 
                  bubbles: true, 
                  cancelable: true 
                });
                checkboxToUncheck.dispatchEvent(changeEvent);
              }, 10);
            }
          });
          
          const buttonInner = document.createElement('span');
          buttonInner.className = 'active-facets__button-inner button button--tertiary';
          
          if (iconHTML) {
            buttonInner.innerHTML = `
              Collections: ${this.escapeHtml(collectionName)}
              <span class="svg-wrapper">
                ${iconHTML}
              </span>
              <span class="visually-hidden">Clear filter</span>
            `;
          } else {
            buttonInner.innerHTML = `
              Collections: ${this.escapeHtml(collectionName)}
              <span class="svg-wrapper">×</span>
              <span class="visually-hidden">Clear filter</span>
            `;
          }
          
          link.appendChild(buttonInner);
          pill.appendChild(link);
          
          // Insert before clear all button
          const clearAllButton = activeFacetsMobile.querySelector('.active-facets__button-wrapper');
          if (clearAllButton) {
            clearAllButton.before(pill);
          } else {
            activeFacetsMobile.appendChild(pill);
          }
        }
      });
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

customElements.define('collection-filter-form', CollectionFilterForm);

// Global filter function that can be called directly
function filterProductsByCollections() {
  const form = document.querySelector('collection-filter-form');
  if (form && form.filterProducts && form.updateURL) {
    // Reset filtering flag
    form._isFiltering = false;
    
    // Filter products first
    form.filterProducts();
    
    // Then update URL after filtering completes to ensure checkbox state is read correctly
    setTimeout(() => {
      if (form && form.updateURL) {
        // Get current checkbox state and update URL
        form.updateURL();
      }
    }, 100);
  }
}

// Set up event delegation on the form/document (only once)
let eventListenersSetup = false;

function setupCollectionFilterCheckboxes() {
  // Only set up once to avoid duplicate listeners
  if (eventListenersSetup) return;
  eventListenersSetup = true;
  
  // Use event delegation - listen on document for checkbox changes
  document.addEventListener('change', function(e) {
    if (e.target && e.target.classList.contains('collection-filter-checkbox')) {
      e.stopPropagation();
      // Small delay to ensure checkbox state is fully updated, then filter and update URL
      setTimeout(() => {
        filterProductsByCollections();
      }, 10);
    }
  }, true); // Use capture phase to catch it early
  
  // Listen for product grid updates (when Shopify filters change via facets.js)
  const productGrid = document.getElementById('product-grid');
  if (productGrid && !window.paginationObserver) {
    window.paginationObserver = new MutationObserver((mutations) => {
      // Skip if we're currently filtering to prevent infinite loop
      const form = document.querySelector('collection-filter-form');
      if (form && form._isFiltering) return;
      
      // Only react to actual product additions/removals, not attribute changes
      let productsAddedOrRemoved = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0)) {
          productsAddedOrRemoved = true;
        }
      });
      
      if (productsAddedOrRemoved) {
        // Re-initialize pagination after grid updates (from Shopify filters)
        setTimeout(() => {
          const form = document.querySelector('collection-filter-form');
          if (form && !form._isFiltering) {
            const items = Array.from(productGrid.querySelectorAll('.grid__item, li[class*="grid__item"]'));
            const visibleCount = items.filter(item => !item.classList.contains('hidden')).length;
            form.updatePagination(visibleCount || items.length, items.length);
          } else if (!form) {
            // Initialize pagination for the first time
            initPaginationOnPageLoad();
          }
        }, 100);
      }
    });
    
    // Only observe childList changes, not attributes (which we control)
    window.paginationObserver.observe(productGrid, {
      childList: true,
      subtree: false  // Only watch direct children, not nested changes
    });
  }
  
}

// Initialize pagination on page load
function initPaginationOnPageLoad() {
  const productGrid = document.getElementById('product-grid');
  if (!productGrid) return;
  
  const productItems = Array.from(productGrid.querySelectorAll('.grid__item, li[class*="grid__item"]'));
  if (productItems.length === 0) return;
  
  // Create a temporary form instance to use updatePagination
  const tempForm = document.createElement('collection-filter-form');
  document.body.appendChild(tempForm);
  
  const visibleCount = productItems.filter(item => !item.classList.contains('hidden')).length;
  tempForm.updatePagination(visibleCount || productItems.length, productItems.length);
  
  // Clean up
  setTimeout(() => {
    document.body.removeChild(tempForm);
  }, 100);
}

// Save and restore collection filter state
function saveCollectionFilterState() {
  const form = document.querySelector('collection-filter-form');
  if (!form) return null;
  
  const checkboxes = form.querySelectorAll('.collection-filter-checkbox:checked');
  const selectedHandles = Array.from(checkboxes).map(cb => cb.dataset.collectionHandle).filter(Boolean);
  return selectedHandles;
}

function restoreCollectionFilterState(selectedHandles) {
  if (!selectedHandles || selectedHandles.length === 0) return;
  
  // Wait for DOM to be fully ready, with retries
  let retries = 0;
  const maxRetries = 10;
  
  const tryRestore = () => {
    const form = document.querySelector('collection-filter-form');
    if (!form) {
      retries++;
      if (retries < maxRetries) {
        setTimeout(tryRestore, 100);
      }
      return;
    }
    
    // Restore checkbox states
    let restoredCount = 0;
    selectedHandles.forEach(handle => {
      const checkbox = form.querySelector(`input[data-collection-handle="${handle}"]`);
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        restoredCount++;
      }
    });
    
    // Re-apply filtering if we restored checkboxes
    if (restoredCount > 0 && form.filterProducts) {
      // Prevent the filtering flag from blocking
      form._isFiltering = false;
      // Small delay to ensure checkboxes are fully set
      setTimeout(() => {
        form.filterProducts();
      }, 50);
    }
  };
  
  setTimeout(tryRestore, 100);
}

// Intercept Shopify's facets.js to preserve collection filter state
function interceptFacetsJS() {
  if (typeof FacetFiltersForm === 'undefined') {
    // Wait for facets.js to load
    setTimeout(interceptFacetsJS, 100);
    return;
  }
  
  // Only intercept once
  if (FacetFiltersForm._collectionFilterIntercepted) return;
  FacetFiltersForm._collectionFilterIntercepted = true;
  
  // Intercept renderFilters to preserve collection filter state
  const originalRenderFilters = FacetFiltersForm.renderFilters;
  FacetFiltersForm.renderFilters = function(html, event) {
    // Save collection filter state from URL as source of truth BEFORE filters are replaced
    const urlParams = new URLSearchParams(window.location.search);
    const collectionsParam = urlParams.get('collections');
    const savedState = collectionsParam ? collectionsParam.split(',').map(h => h.trim()).filter(Boolean) : saveCollectionFilterState();
    
    // Call original method to render filters
    originalRenderFilters.call(this, html, event);
    
    // Restore collection filter state after filters are rendered
    if (savedState && savedState.length > 0) {
      setTimeout(() => {
        // Re-initialize collection filters
        eventListenersSetup = false; // Allow re-setup after DOM reload
        setupCollectionFilterCheckboxes();
        
        // Restore checkbox states
        restoreCollectionFilterState(savedState);
      }, 200);
    } else {
      // Still re-initialize even if no state to restore
      setTimeout(() => {
        eventListenersSetup = false;
        setupCollectionFilterCheckboxes();
      }, 200);
    }
  };
  
  const originalRenderProductGridContainer = FacetFiltersForm.renderProductGridContainer;
  FacetFiltersForm.renderProductGridContainer = function(html) {
    // Save collection filter state from URL as source of truth
    const urlParams = new URLSearchParams(window.location.search);
    const collectionsParam = urlParams.get('collections');
    const savedState = collectionsParam ? collectionsParam.split(',').map(h => h.trim()).filter(Boolean) : saveCollectionFilterState();
    
    // Call original method
    originalRenderProductGridContainer.call(this, html);
    
    // Restore filtering after product grid is updated
    if (savedState && savedState.length > 0) {
      setTimeout(() => {
        const form = document.querySelector('collection-filter-form');
        if (form && form.filterProducts) {
          form._isFiltering = false;
          form.filterProducts();
        }
      }, 400);
    }
  };
  
  // Fix ProductCount error - check if element exists before setting innerHTML
  const originalRenderProductCount = FacetFiltersForm.renderProductCount;
  FacetFiltersForm.renderProductCount = function(html) {
    try {
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const countElement = parsedHtml.getElementById('ProductCount');
      if (!countElement) {
        // Element doesn't exist in the new HTML, just hide loading spinners
        const loadingSpinners = document.querySelectorAll(
          '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
        );
        loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
        return;
      }
      
      const count = countElement.innerHTML;
      const container = document.getElementById('ProductCount');
      const containerDesktop = document.getElementById('ProductCountDesktop');
      
      if (container) {
        container.innerHTML = count;
        container.classList.remove('loading');
      }
      if (containerDesktop) {
        containerDesktop.innerHTML = count;
        containerDesktop.classList.remove('loading');
      }
      
      const loadingSpinners = document.querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
      );
      loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
    } catch (e) {
      // Silently fail - not critical
    }
  };
}

// Start intercepting facets.js
interceptFacetsJS();

// Handle URL parameters on page load and set up checkboxes
function initCollectionFilters() {
  const urlParams = new URLSearchParams(window.location.search);
  const collectionsParam = urlParams.get('collections');
  
  // Set up event listeners first
  setupCollectionFilterCheckboxes();
  
  // Then restore checkbox states from URL
  if (collectionsParam) {
    setTimeout(() => {
      const selectedHandles = collectionsParam.split(',').map(h => h.trim()).filter(Boolean);
      const form = document.querySelector('collection-filter-form');
      if (form && selectedHandles.length > 0) {
        selectedHandles.forEach(handle => {
          const checkbox = form.querySelector(`input[data-collection-handle="${handle}"]`);
          if (checkbox) {
            checkbox.checked = true;
          }
        });
        
        // Apply filtering after restoring checkboxes
        if (form.filterProducts) {
          form._isFiltering = false; // Reset flag
          form.filterProducts();
        }
      }
    }, 200);
  }
  
  // Initialize pagination on page load after a short delay
  setTimeout(() => {
    initPaginationOnPageLoad();
  }, 300);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionFilters);
} else {
  // If DOM is already loaded, wait for custom elements
  setTimeout(initCollectionFilters, 200);
}

// Also try to set up when collection-filter-form is added to DOM
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) { // Element node
        if (node.tagName === 'COLLECTION-FILTER-FORM' || node.querySelector('collection-filter-form')) {
          // Reset flag to allow re-setup
          eventListenersSetup = false;
          
          setTimeout(() => {
            setupCollectionFilterCheckboxes();
            
            // Restore state from URL
            const urlParams = new URLSearchParams(window.location.search);
            const collectionsParam = urlParams.get('collections');
            if (collectionsParam) {
              const selectedHandles = collectionsParam.split(',').map(h => h.trim()).filter(Boolean);
              restoreCollectionFilterState(selectedHandles);
            }
          }, 150);
        }
      }
    });
  });
});

observer.observe(document.body, {
  childList: true,
  subtree: true
});

